// Plain-English breakdown of the subject's conditions, with citations to primary
// law. Sourced from docs/COMPLIANCE-SOURCE-OF-TRUTH.md (researched & adversarially
// verified against legislation.gov.uk, current to 25 June 2026).
// This is legal INFORMATION, not advice.

export type Cite = { label: string; url?: string };
export type Duty = { title: string; plain: string; deadline?: string; tag?: string; cites: Cite[] };
export type Prohibition = { n: number; title: string; plain: string; duty?: string; cites: Cite[] };

const L = (label: string, section: string): Cite => ({
  label,
  url: `https://www.legislation.gov.uk/${section}`,
});
const GOV = (label: string, url: string): Cite => ({ label, url });
export const PROVENANCE =
  'This page is compiled from primary UK legislation (legislation.gov.uk, current to 25 June 2026) ' +
  'and official Government guidance, and cross-checked against those sources. It is written so you ' +
  'can refer to it and show it to your offender manager, the police Public Protection Unit (PPU), or ' +
  'a court, and trace every point to its source. It is legal INFORMATION, not legal advice, and it ' +
  'does not replace your sealed court order — where the two differ, the order governs.';

export const CITATION_KEY: { marker: string; text: string }[] = [
  { marker: 'legislation.gov.uk links', text: 'the actual primary statute or regulation (current to 25 Jun 2026).' },
  { marker: '"SHPO §n"', text: 'the numbered prohibition in your sealed Sexual Harm Prevention Order (Schedule of Prohibitions, 19 Dec 2022) — the binding source for that exact term; the link is to the statutory power it was made under (Sentencing Act 2020 s.343).' },
  { marker: 'GOV.UK / CPS links', text: 'official Government, DBS or CPS guidance.' },
  { marker: '"confirm with PPU / solicitor"', text: 'a point of interpretation not settled by statute — a flag to resolve, not a ruling.' },
];

export const ORDERS_FRAMEWORK: Duty[] = [
  {
    title: 'Why you are on the register, and for how long',
    plain:
      'You were convicted of an offence listed in Schedule 3 to the Sexual Offences Act 2003, so you ' +
      'are subject to the notification requirements in Part 2 of that Act. The length is fixed ' +
      'automatically by s.82 — a 10-month sentence falls in the "more than 6 but less than 30 months" ' +
      'band = 10 years — running from your conviction date (25 Oct 2022).',
    cites: [L('SOA 2003 s.80', 'ukpga/2003/42/section/80'), L('SOA 2003 s.82', 'ukpga/2003/42/section/82')],
  },
  {
    title: 'How your SHPO was made and what limits it',
    plain:
      'Your Sexual Harm Prevention Order was made on conviction under the Sentencing Act 2020 (s.345). ' +
      'A court may impose or keep only terms that are necessary to protect the public, or children, from ' +
      'sexual harm (s.346). It must last at least 5 years (yours is 10) (s.347), and it keeps you subject ' +
      'to the notification requirements for its duration (SOA 2003 s.103G).',
    cites: [
      L('Sentencing Act 2020 s.345', 'ukpga/2020/17/section/345'),
      L('s.346', 'ukpga/2020/17/section/346'),
      L('s.347', 'ukpga/2020/17/section/347'),
      L('SOA 2003 s.103G', 'ukpga/2003/42/section/103G'),
    ],
  },
  {
    title: 'Changing or ending your SHPO',
    plain:
      'You can apply to the Crown Court (the court that made it) to vary or discharge the order. It cannot ' +
      'be discharged before 19 Dec 2027 without the consent of both you and the police. This is the proper ' +
      'route if a genuine need (for example a work requirement) conflicts with one of your terms.',
    cites: [L('Sentencing Act 2020 s.350', 'ukpga/2020/17/section/350')],
  },
];

export const CASE = [
  ['Offence', 'Making indecent photographs of a child (Protection of Children Act 1978, s.1)'],
  ['Court / case', 'Liverpool Crown Court · T20211216'],
  ['Convicted', '25 October 2022'],
  ['Sentenced', '19 December 2022'],
  ['Sentence', '10 months imprisonment, suspended 24 months (+120h unpaid work, up to 30 RAR days)'],
  ['On the register ("notification")', '10 years — until ~24 October 2032'],
  ['Sexual Harm Prevention Order', '10 years — until ~19 December 2032 (no foreign-travel ban)'],
] as const;

export const FLAGS = [
  {
    kind: 'bad' as const,
    title: 'Annual re-notification may be overdue — check with your PPU',
    body:
      'Your records show "Last Notification: 22/11/2024". Re-notification is due within 12 months (by ~21 Nov 2025). As at 25 Jun 2026 that window appears to have passed — unless you have already re-notified since and the record is simply out of date. Missing it is itself an offence. Confirm your last and next dates with your PPU as a priority.',
  },
  {
    kind: 'warn' as const,
    title: 'The suspended sentence has likely ended',
    body:
      'The 24-month operational period ran from 19/12/2022 and so expired ~18 December 2024, and the unpaid-work deadline was the same date. Those obligations are most likely complete (confirm with probation). The SHPO and the register both continue until ~2032.',
  },
];

export const NOTIFY_DUTIES: Duty[] = [
  {
    title: 'Annual re-notification',
    plain:
      'You must go to your police station and re-confirm your details at least once every 12 months, even if nothing has changed. The clock restarts each time you notify.',
    deadline: 'Within 12 months of your last notification',
    tag: 'Reporting duty',
    cites: [L('SOA 2003 s.85', 'ukpga/2003/42/section/85')],
  },
  {
    title: 'Change of name, home address, or staying elsewhere',
    plain:
      'Tell the police within 3 days if you use a new name, change your home address, or stay at another address for 7 days (or 7 days in total across any 12 months).',
    deadline: 'Within 3 days',
    tag: 'Reporting duty',
    cites: [L('SOA 2003 s.84', 'ukpga/2003/42/section/84')],
  },
  {
    title: 'Bank accounts and credit/debit cards',
    plain:
      'You must notify all bank/building-society accounts and credit/debit cards in your name, held jointly, or in a business name — including account numbers, sort codes, and card numbers/expiry — and any change to them.',
    deadline: 'Changes within 3 days',
    tag: 'Reporting duty',
    cites: [L('SI 2012/1876 reg 12–13', 'uksi/2012/1876/regulation/12/made')],
  },
  {
    title: 'Passport and identity documents',
    plain: 'Notify your passport (number + name as shown) and ID documents, and tell police if you get or stop holding one.',
    deadline: 'Changes within 3 days',
    tag: 'Reporting duty',
    cites: [L('SI 2012/1876 reg 14–15', 'uksi/2012/1876/regulation/14/made')],
  },
  {
    title: 'Staying where a child under 18 is present (12-hour rule)',
    plain:
      'If you reside, or stay for 12 hours or more, at any household or non-public place where a person under 18 lives or stays, you must notify the date, the address, and how long you intend to stay. Treat any overnight or 12h+ stay where an under-18 is present as notifiable.',
    deadline: 'Within 3 days',
    tag: 'Reporting duty · important',
    cites: [L('SI 2012/1876 reg 10–11', 'uksi/2012/1876/regulation/10/made')],
  },
  {
    title: 'Foreign travel',
    plain:
      'Your SHPO does NOT ban foreign travel — but you must notify every trip outside the UK (even a day trip): normally at least 7 days before (or at least 12 hours before if you only just learned the details), giving dates, countries, points of arrival, accommodation and carriers. Notify any change before you go, and notify your return within 3 days (unless you returned exactly as pre-notified).',
    deadline: '≥7 days before departure',
    tag: 'Reporting duty',
    cites: [
      L('SOA 2003 s.86', 'ukpga/2003/42/section/86'),
      L('Travel Regs 2004 (as amended)', 'uksi/2012/1876/regulation/5/made'),
    ],
  },
  {
    title: 'How you notify, and your proof',
    plain:
      'Every notification is made IN PERSON at a designated police station in your area, given orally. The police must give you a WRITTEN ACKNOWLEDGEMENT — keep every one as your proof you complied. If asked, you must allow fingerprints/a photograph to verify identity (refusing is itself an offence). Confirm with your PPU which station and what to bring. ' +
      'EMAIL, PHONE AND LETTER DO NOT COUNT as notification: because s.87 requires you to attend and notify orally, you have not notified — and the clock does not stop — until you have physically attended and obtained the written acknowledgement. (Your own records confirm this: the police unit expressly refused to accept travel notification by email and directed you to attend in person.) Use any email only to arrange the appointment or to evidence that you tried to comply promptly — the compliant act is always the in-person attendance.',
    tag: 'Method',
    cites: [L('SOA 2003 s.87', 'ukpga/2003/42/section/87')],
  },
];

export const PROHIBITIONS: Prohibition[] = [
  {
    n: 1,
    title: 'Internet-capable devices — only with history + 3-day notification',
    plain:
      'You may not use any internet-capable device unless (i) it can retain and display its internet history, (ii) you notify the PPU within 3 days of acquiring it, and (iii) you make it available to police for inspection / Risk-Management Software. (Workplace, library, Job Centre, educational use is exempt — but for work use you must notify the PPU within 3 days of starting.)',
    duty: 'App tracks each device + a 3-day acquisition reminder',
    cites: [L('SHPO §1 (Sentencing Act 2020 s.343)', 'ukpga/2020/17/section/343')],
  },
  { n: 2, title: 'No deleting internet history', plain: 'You may not delete your user internet history on any such device.', cites: [L('SHPO §2', 'ukpga/2020/17/section/343')] },
  { n: 3, title: 'No history-erasing operating systems', plain: 'You may not install or use any operating system that removes all traces of internet history (e.g. "TAILS", "Whonix").', cites: [L('SHPO §3', 'ukpga/2020/17/section/343')] },
  { n: 4, title: 'No interfering with Risk-Management Software', plain: 'You may not delete or interfere with any Risk-Management Software installed on a device.', cites: [L('SHPO §4', 'ukpga/2020/17/section/343')] },
  { n: 5, title: 'No social networking / file-sharing / forums / chat', plain: 'You may not create membership of, access or use any online social-networking site, file-sharing site, forum, bulletin board, chat room or similar communication medium.', duty: 'The optional logins/social vault is for record-keeping only — never operating accounts', cites: [L('SHPO §5', 'ukpga/2020/17/section/343')] },
  { n: 6, title: 'Home broadband router available for inspection', plain: 'You may not use a home broadband router unless you make it available for police inspection on request.', duty: 'App flags the router for inspection-readiness', cites: [L('SHPO §6', 'ukpga/2020/17/section/343')] },
  { n: 7, title: 'No message-destroying apps', plain: 'You may not install or use any app that destroys messages sent or received (e.g. "Snapchat", "Wickr").', cites: [L('SHPO §7', 'ukpga/2020/17/section/343')] },
  { n: 8, title: 'No evidence-elimination software', plain: 'You may not install or use evidence-elimination software (e.g. "CCleaner") beyond what is intrinsic to a device working normally.', cites: [L('SHPO §8', 'ukpga/2020/17/section/343')] },
  { n: 9, title: 'No VPN / proxy that hides your IP', plain: 'You may not install or use any VPN or proxy that can anonymise your IP address (e.g. "hidemyass", "ipvanish", "ExpressVPN").', cites: [L('SHPO §9', 'ukpga/2020/17/section/343')] },
  {
    n: 10,
    title: 'Cloud storage — only with 3-day notification + access',
    plain: 'You may not use cloud-based internet storage unless you (i) notify the PPU within 3 days of creating the account, and (ii) make its contents available for immediate viewing on request.',
    duty: 'App tracks each cloud account + a 3-day creation reminder',
    cites: [L('SHPO §10', 'ukpga/2020/17/section/343')],
  },
  { n: 11, title: 'No bypassing monitoring software', plain: 'You may not interfere with or bypass the normal running of any computer monitoring software.', cites: [L('SHPO §11', 'ukpga/2020/17/section/343')] },
  {
    n: 12,
    title: 'Image-capable devices available for inspection',
    plain: 'You may not possess any device capable of storing digital images (moving or still) unless you provide access to that storage for inspection on request.',
    duty: 'App flags image-capable devices for inspection-readiness',
    cites: [L('SHPO §12', 'ukpga/2020/17/section/343')],
  },
];

export const EMPLOYMENT_INTRO =
  'You are not banned from working — you can be employed or self-employed. But three things apply: ' +
  'the device rules (below), a bar on working with children, and what you must disclose. The most ' +
  'important distinction is between a computer at your employer’s premises and a device you own ' +
  'and use for your own business — they are treated very differently.';

export const EMPLOYMENT_KEY_DISTINCTION = {
  title: 'Work computer vs your own business device — the crucial difference',
  body:
    'The SHPO exemption covers a computer AT your place of work (your employer’s computer), a ' +
    'library, Job Centre or educational establishment. It does NOT cover devices you own and use for ' +
    'your own business (e.g. your photo-booth laptops, mini-PCs, tablets and cameras). Anything you ' +
    'own is your device, so the full SHPO §1/§12 rules apply to it — retain/display internet history, ' +
    'notify the PPU within 3 days of acquiring it, and make it available for inspection.',
};

export const EMPLOYMENT: Duty[] = [
  {
    title: 'What counts as a "device"',
    plain:
      'The order bites on any computer or device capable of accessing the internet (§1) and any device ' +
      'capable of storing digital images, moving or still (§12). That includes phones, laptops, tablets, ' +
      'mini-PCs, cameras, games consoles, smart speakers and smart TVs — whether for personal or business use.',
    tag: 'Definition',
    cites: [L('SHPO §1 & §12 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Using a computer at your employer’s workplace — the exemption',
    plain:
      'The internet-device conditions do not apply to a computer at your place of work (or a public ' +
      'library, Job Centre Plus or educational establishment). BUT for work you must notify the PPU ' +
      'within 3 days of starting to use such a computer. This is in your order.',
    deadline: 'Notify PPU within 3 days of starting work-computer use',
    tag: 'Reporting duty',
    cites: [L('SHPO §1 work exemption (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Your own work/business devices are NOT exempt',
    plain:
      'Devices you own and use for your business are treated as your devices, not "workplace computers". ' +
      'The full rules apply: they must retain and display internet history, you must notify the PPU within ' +
      '3 days of acquiring them, and make them (and any image storage) available for inspection on request.',
    tag: 'Important',
    cites: [L('SHPO §1, §6, §12 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Working with children — the DBS barred list & disclosure',
    plain:
      'Whether you are on the DBS Children’s Barred List is a SEPARATE decision by the DBS — it does not ' +
      'follow automatically from a conviction. You understand you are NOT currently barred (you can confirm ' +
      'your status directly with the DBS). The criminal offence of doing "regulated activity" with children ' +
      '(up to 5 years) applies ONLY to a person who is barred — so if you are not barred it does not bite. ' +
      'But note: an enhanced DBS check still DISCLOSES your conviction (so regulated roles with children will ' +
      'see it), and the DBS could bar you in future. Given the offence, working with children is high-risk — ' +
      'take advice before any such role.',
    tag: 'Prohibition',
    cites: [
      L('Safeguarding Vulnerable Groups Act 2006 s.7', 'ukpga/2006/47/section/7'),
      GOV('GOV.UK — regulated activity with children', 'https://www.gov.uk/government/publications/dbs-guidance-leaflets/regulated-activity-with-children'),
    ],
  },
  {
    title: 'DBS checks will disclose your conviction',
    plain:
      'For roles eligible for a standard or enhanced DBS check (typically anything involving children or ' +
      'vulnerable adults), your conviction — and your barred status — will be disclosed to the employer. ' +
      'Answer DBS checks truthfully; never seek barred work.',
    tag: 'Disclosure',
    cites: [GOV('GOV.UK — Disclosure and Barring Service', 'https://www.gov.uk/government/organisations/disclosure-and-barring-service/about')],
  },
  {
    title: 'Do you have to tell the police your employer?',
    plain:
      'There is no general notification duty to tell the police your employer or job — employment is not on ' +
      'the statutory list of things you must notify. BUT: (a) using a work computer must be notified to the ' +
      'PPU within 3 days (which identifies the workplace); (b) your offender manager and the police will ' +
      'normally ask about your employment as part of supervision/risk management (MAPPA) and you should be ' +
      'open with them; and (c) the police may disclose your conviction to an employer where necessary to ' +
      'protect children or the public — especially for any role involving contact with children.',
    tag: 'Disclosure',
    cites: [L('SOA 2003 s.83 (notification list — no employer)', 'ukpga/2003/42/section/83')],
  },
];

export const EXEMPTION_NARROW = {
  title: 'Important: the work-device exemption is NARROW',
  body:
    'The exemption only switches off the §1 device-conditions (history-retention, the 3-day ' +
    'acquire-notification, and the inspection / Risk-Management requirement) for a genuine ' +
    'employer’s computer — and only if you notify the PPU within 3 days of starting to use it. It ' +
    'does NOT lift any of your other prohibitions. Every one of §2–§12 still applies on a work ' +
    'device — including the social-media ban (§5) and the VPN ban (§9). "My job requires it" is not ' +
    'a defence to those.',
};

export const WORK_CANNOT = [
  'Use or access social media, file-sharing sites, forums or chat rooms — even if your job asks you to (§5)',
  'Use a VPN or proxy that can hide your IP address — including many corporate/work VPNs (§9)',
  'Delete your internet history (§2)',
  'Use an incognito / anonymising operating system such as TAILS or Whonix (§3)',
  'Use apps that auto-destroy messages, e.g. Snapchat or Wickr (§7)',
  'Use evidence-elimination software, e.g. CCleaner (§8)',
  'Interfere with, bypass or disable monitoring / Risk-Management Software (§4, §11)',
];

export const WORK_CAN = [
  'Use your employer’s workplace computer for ordinary work (admin, development, architecture) — the §1 device-conditions don’t apply to it, BUT you must notify the PPU within 3 days of starting to use it',
  'Use their normal business software and systems — provided the task doesn’t involve any of the banned activities listed opposite',
  'Access the internet for work on that computer (it need not retain history the way your own devices must) — but every other prohibition still applies',
];

export const WORK_SCENARIOS: Duty[] = [
  {
    title: 'Social media because your job needs it',
    plain:
      '§5 bans using social media, forums and chat outright — a work requirement is not a defence. ' +
      'If a role genuinely needs it, do NOT do it: have that task reassigned, or apply to vary your ' +
      'SHPO (s.350) with a solicitor first. Doing it because work asked you to would still be a breach.',
    tag: 'Prohibited',
    cites: [
      L('SHPO §5 (your order)', 'ukpga/2020/17/section/343'),
      L('Vary the order — Sentencing Act 2020 s.350', 'ukpga/2020/17/section/350'),
    ],
  },
  {
    title: 'VPNs — including your employer’s corporate VPN',
    plain:
      '§9 bans any VPN or proxy that can anonymise your IP address, and a corporate/work VPN may well ' +
      'be caught by that wording. Do NOT use any VPN for work until you have written confirmation from ' +
      'your PPU (and ideally a solicitor) about the specific VPN, or the order is varied. This is a real ' +
      'risk for systems/administration/architecture roles that rely on VPN access.',
    tag: 'High-risk',
    cites: [L('SHPO §9 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Creating cloud storage for work',
    plain:
      'The §10 duty still applies — notify the PPU within 3 days of creating any cloud-storage account ' +
      'and make its contents available on request. The work exemption is attached to §1, not §10, so a ' +
      'work cloud account is still notifiable.',
    deadline: 'Notify PPU within 3 days of creating a cloud account',
    tag: 'Reporting duty',
    cites: [L('SHPO §10 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Using their systems generally',
    plain:
      'Ordinary admin, development and architecture work on your employer’s systems is fine — except ' +
      'where a task touches a banned activity (social media, a VPN, creating cloud accounts, forums, or ' +
      'bypassing monitoring). When a task strays into any of those, stop and ask the PPU before doing it.',
    tag: 'Allowed (with limits)',
    cites: [L('SHPO §1, §5, §9, §10 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Working from home on your own device',
    plain:
      'A device YOU own that you use for work — even your employer’s work — is your device, not "a ' +
      'computer at your place of work". The full §1/§12 rules apply: it must retain/display history, ' +
      'you must notify the PPU within 3 days of acquiring it, and make it available for inspection. The ' +
      'workplace exemption only covers a computer at the employer’s premises.',
    tag: 'Your device — full rules',
    cites: [L('SHPO §1 & §12 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Devices your own company (Candid Camera Events Ltd) owns that YOU use',
    plain:
      'Don’t rely on the "place of work" exemption for kit your own company owns and you control as a ' +
      'director — that exemption is meant for an outside employer’s monitored computer, not your own ' +
      'business equipment. Treat company devices you use as your own: retain history, notify the PPU ' +
      'within 3 days of starting to use/acquire them, and make them (and any image storage) available ' +
      'for inspection. Tell the PPU about the arrangement.',
    tag: 'Treat as your own',
    cites: [L('SHPO §1, §6, §12 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Company devices you issue to employees (that you don’t personally use)',
    plain:
      'This is a genuine grey area. As a director you may be treated as controlling, or having access ' +
      'to, your company’s devices, and §12 "possession" is wide. The safe course is to set out the ' +
      'whole arrangement to your PPU/offender manager in writing and follow their direction — do not ' +
      'rely on a technical "I don’t personally use it" reading.',
    tag: 'Grey area — disclose',
    cites: [L('SHPO §1 & §12 (your order)', 'ukpga/2020/17/section/343')],
  },
];

export const GREY_AREA = {
  title: 'Get the grey areas confirmed in writing — before you act',
  body:
    'Company-owned devices, your director responsibilities, working from home on your own kit, VPNs, ' +
    'and any social-media or cloud requirements of your two roles (director of Candid Camera Events ' +
    'Ltd, and PAYE office-admin/systems work for the minibus company) are exactly the situations where ' +
    'people breach without meaning to — and breach carries up to 5 years. Before you start or continue ' +
    'any role that touches these, set out the full picture to your offender manager and PPU, get their ' +
    'answer in writing, and take legal advice. If a role genuinely needs an otherwise-prohibited ' +
    'activity, the proper route is to apply to vary your SHPO (Sentencing Act 2020 s.350) — not to rely ' +
    'on a work justification.',
};

export const DEVICE_INTRO =
  'Device notification comes from your SHPO (§1 for internet-capable devices, §12 for anything that ' +
  'can store images), not from the general "register" list. The order tells you WHEN to notify and ' +
  'that the device must be available for inspection — but it does not, on its face, set out exactly ' +
  'what details to give. So the safe approach is: give full identifying details, keep a dated record, ' +
  'and ask your PPU what they record.';

export const DEVICE_RULES: Duty[] = [
  {
    title: 'What the order actually requires',
    plain:
      'You must notify the PPU within 3 days of ACQUIRING an internet-capable device, retain/display its ' +
      'internet history, and make it available for inspection. The order does not list specific details — ' +
      'it does not, on its face, demand serial numbers — so what is recorded is an operational matter for ' +
      'the police.',
    deadline: 'Notify within 3 days of acquiring',
    tag: 'Order requirement',
    cites: [L('SHPO §1 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'What to give them in practice',
    plain:
      'Give enough to identify the device unambiguously: make, model, type, and the serial number / IMEI ' +
      'where it has one, plus where it is kept and who uses it. Ask your PPU exactly what they want ' +
      'recorded, give full specifics, and keep your own dated proof — that is what this system is for.',
    tag: 'Best practice',
    cites: [L('SHPO §1 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Work computers — is it just "I use a computer at work"?',
    plain:
      'For the workplace exemption you must notify the PPU within 3 days of commencing use of a work ' +
      'computer. The exemption turns on the fact of using a computer at your place of work — tell them the ' +
      'employer/workplace and that you are using their computer(s). Whether they also want make/model/serial ' +
      'is operational: ask your PPU and follow what they say.',
    deadline: 'Notify within 3 days of starting work-computer use',
    tag: 'Order requirement',
    cites: [L('SHPO §1 work exemption (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'If a device breaks, is lost, stolen, or you dispose of it',
    plain:
      'The order does not spell these out, but tell the PPU promptly and keep a dated record. You cannot ' +
      'make a lost/stolen device available for inspection, so disclose it; keeping the police record current ' +
      'also avoids the "your list is out of date" problem. A REPLACEMENT device is a new acquisition — notify ' +
      'it within 3 days.',
    tag: 'Confirm with PPU',
    cites: [L('SHPO §1 (your order)', 'ukpga/2020/17/section/343')],
  },
  {
    title: 'Repairs that replace storage (e.g. your photo-booth PC’s failed hard drive)',
    plain:
      'This is the important one. Replacing a failed hard drive is a grey area: the machine is arguably the ' +
      'same device, but the new drive wipes the previous internet history — which could be MISREAD as ' +
      'deliberately deleting history (§2). Protect yourself: record exactly what happened and when (failure ' +
      'date, replacement date, reason) and tell your PPU, so a genuine hardware failure cannot be mistaken ' +
      'for a breach. Your recent hard-drive replacement is exactly this — log it and mention it to your PPU.',
    tag: 'Important — log it',
    cites: [
      L('SHPO §1 (your order)', 'ukpga/2020/17/section/343'),
      L('SHPO §2 — no deleting history (your order)', 'ukpga/2020/17/section/343'),
    ],
  },
];

export const DEVICE_CALLOUT = {
  title: 'When in doubt: give full details, record it, and ask the PPU',
  body:
    'The order sets the duty (notify acquisition within 3 days + make the device available for inspection) ' +
    'but leaves the level of detail, and edge-cases like loss and repair, to be handled with your PPU. The ' +
    'safe rule is to give full identifying details, keep a dated, tamper-evident record (the purpose of this ' +
    'system), and — wherever the order is not explicit (loss, breakage, repairs, what details to record) — ' +
    'ask your PPU and get the answer in writing.',
};

export const CONSEQUENCES = [
  {
    title: 'Breaching the notification (reporting) duties',
    plain:
      'Failing — without reasonable excuse — to comply with any notification duty, refusing fingerprints/photographs, or giving information you know to be false, is a criminal offence. It is a continuing offence and carries up to 5 years’ imprisonment on indictment (or up to 6 months and/or an unlimited fine summarily).',
    cites: [L('SOA 2003 s.91', 'ukpga/2003/42/section/91')],
  },
  {
    title: 'Breaching the SHPO',
    plain:
      'Doing anything the SHPO prohibits, or failing to do anything it requires, without reasonable excuse, is a criminal offence carrying up to 5 years’ imprisonment. A conditional discharge is not available for breach.',
    cites: [
      L('Sentencing Act 2020 s.354', 'ukpga/2020/17/section/354'),
      L('SOA 2003 s.103I', 'ukpga/2003/42/section/103I'),
    ],
  },
];

export const KEY_DATES = [
  ['25 Oct 2022', 'Conviction — notification period starts'],
  ['19 Dec 2022', 'Sentence; SHPO made'],
  ['~18 Dec 2024', 'Suspended sentence period ends; unpaid-work deadline (now passed)'],
  ['~21 Nov 2025', 'Annual re-notification due (12 months after the last on file) — see flag above'],
  ['19 Dec 2027', 'Earliest the SHPO could be discharged (needs dual consent before then)'],
  ['~24 Oct 2032', 'Notification period ends automatically'],
  ['~19 Dec 2032', 'SHPO ends (unless varied/discharged earlier)'],
] as const;

export const SOURCES: { group: string; items: Cite[] }[] = [
  {
    group: 'Primary legislation (legislation.gov.uk)',
    items: [
      L('Sexual Offences Act 2003, Part 2 (notification) — ss.80–93', 'ukpga/2003/42/part/2'),
      L('SOA 2003 s.82 (notification period)', 'ukpga/2003/42/section/82'),
      L('SOA 2003 s.83 / s.84 / s.85 / s.87 (notify, changes, annual, method)', 'ukpga/2003/42/section/83'),
      L('SOA 2003 s.86 (foreign travel) & s.91 (offences/penalty)', 'ukpga/2003/42/section/91'),
      L('SOA 2003 s.103G / s.103I (SHPO notification effect / breach)', 'ukpga/2003/42/section/103I'),
      L('SI 2012/1876 — Notification Requirements Regs 2012 (bank/cards, passport, child-household)', 'uksi/2012/1876/contents/made'),
      L('SI 2004/1220 — Travel Notification Requirements Regs 2004 (as amended)', 'uksi/2004/1220/contents/made'),
      L('Sentencing Act 2020, Part 11 — SHPOs (ss.343–358)', 'ukpga/2020/17/part/11'),
      L('Safeguarding Vulnerable Groups Act 2006 s.7 (barred person — regulated activity)', 'ukpga/2006/47/section/7'),
    ],
  },
  {
    group: 'Official guidance',
    items: [
      GOV('Home Office — Guidance on Part 2 of the SOA 2003', 'https://www.gov.uk/government/publications/guidance-on-part-2-of-the-sexual-offences-act-2003/guidance-on-part-2-of-the-sexual-offences-act-2003-accessible-version'),
      GOV('GOV.UK — Regulated activity with children (DBS)', 'https://www.gov.uk/government/publications/dbs-guidance-leaflets/regulated-activity-with-children'),
      GOV('CPS — Chapter 15: Sexual Harm Prevention Orders', 'https://www.cps.gov.uk/prosecution-guidance/rape-and-sexual-offences-chapter-15-sexual-harm-prevention-orders-shpos'),
    ],
  },
  {
    group: 'Your sealed court orders (the authoritative source for your specific terms — held privately)',
    items: [
      { label: 'Certificate of Conviction & notification notice — Liverpool Crown Court, 19 Dec 2022' },
      { label: 'Sexual Harm Prevention Order — Schedule of Prohibitions, 19 Dec 2022' },
      { label: 'Suspended Sentence Order — 19 Dec 2022' },
    ],
  },
];
